import {
  ConfigObjectType,
  PaginatedResponse,
  serializeJSONToString,
  transformUrlSearchParams,
  useApiQuery,
} from '@wallaby-ui/core';
import timelineDataMock from '@wallaby-ui/merie/src/js/mocks/feature/hospitalization_detail/hospitalization_timeline_data_mock.json';
import { debounce, isEqual, omit } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { getFlattenApiParametersSelector } from 'src/js/containers/filter_and_search/filter_and_search_selectors';
import { TableConfig } from 'src/js/model/table_config';
import { RootState } from 'src/js/store';
import {
  enhanceGridSortWithAdditionalSort,
  getGridApiStateId,
} from '../grid_helpers';
import {
  getGridConfig,
  getGridPagination,
  getGridParams,
  getGridSort,
  getGridStateId,
} from '../grid_selectors';
import { gridSlice } from '../grid_slice';
import {
  changeGridParamsAndQuery,
  changeMemorizeColumnStates,
  loadGridConfig,
  recoverGridParams,
} from '../grid_thunks';
import {
  AgGridPagination,
  MemorizedColumnStates,
  Pagination,
  SelectedRow,
} from '../grid_types';
import AgGridWrapper from './AgGridWrapper';

export interface FetchedDataGridContainerOwnProps {
  filterAndSearchStateId?: string;
  config: ConfigObjectType;
  configPath: string;
  domLayout?: string;
  gridId: string;
  gridStateId?: string;
  apiUrl: string;
  onLoadingChange?: (isLoading: boolean) => void;
  onDataLoaded?: (data: PaginatedResponse | undefined) => void;
  handlePaginationDataChange?: (count?: number) => void;
  onGridInitialize?: () => void;
  disablePagination?: boolean;
  showPageSize?: boolean;
  additionalApiParameters?: Record<string, any>;
  selectedRow?: SelectedRow;
  dataTransformFunction?: (data: any) => any;
  featureName?: string;
}

const mapState = (
  state: RootState,
  props: FetchedDataGridContainerOwnProps
) => ({
  gridStateId: getGridStateId(state, props),
  gridConfig: getGridConfig(state, props),
  currentSort: getGridSort(state, props),
  gridParams: getGridParams(state, props),
  gridPagination: getGridPagination(state, props),
  apiParameters: getFlattenApiParametersSelector(state, props),
  gridApiParameters: state[getGridApiStateId(props.gridId)]?.apiParameters,
  memorizedColumnStates:
    state[getGridStateId(state, props)]?.memorizedColumnStates,
});

const mapDispatch = {
  changeGridParams: gridSlice.actions.changeGridParams,
  initGridParams: gridSlice.actions.initGridParams,
  changeGridParamsAndQueryAC: changeGridParamsAndQuery,
  loadGridConfigAC: loadGridConfig,
  recoverGridParamsAC: recoverGridParams,
  changeMemorizeColumnStatesAC: changeMemorizeColumnStates,
};

const connector = connect(mapState, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & FetchedDataGridContainerOwnProps;

const FetchedDataGridContainer: React.FC<Props> = (props) => {
  const {
    config,
    apiParameters,
    configPath,
    domLayout = 'normal',
    gridConfig,
    disablePagination = false,
    gridPagination,
    currentSort,
    gridId,
    gridParams,
    recoverGridParamsAC,
    gridStateId,
    filterAndSearchStateId,
    loadGridConfigAC,
    apiUrl,
    additionalApiParameters,
    initGridParams,
    changeGridParamsAndQueryAC,
    onDataLoaded,
    onLoadingChange,
    gridApiParameters,
    selectedRow,
    onGridInitialize,
    handlePaginationDataChange,
    showPageSize,
    dataTransformFunction,
    featureName,
    changeMemorizeColumnStatesAC,
    memorizedColumnStates,
  } = props;

  const [initialized, setInitialized] = useState<boolean>(false);

  const gridsApiStateId = getGridApiStateId(gridId);

  const gridConfigModel = useMemo<TableConfig>(() => {
    return new TableConfig(gridConfig);
  }, [gridConfig]);

  useEffect(() => {
    if (!gridConfig) {
      loadGridConfigAC(gridStateId, configPath);
    }
  }, [configPath, loadGridConfigAC, gridConfig, gridStateId]);

  useEffect(() => onGridInitialize && onGridInitialize(), [onGridInitialize]);

  useEffect(() => {
    if (!initialized) {
      initGridParams(gridStateId, {
        params: { defaultPagination: { ...gridPagination } },
      });
      setInitialized(true);
    }
  }, [
    initGridParams,
    initialized,
    gridConfig,
    gridId,
    gridPagination,
    gridStateId,
  ]);

  const globalApiParameters = {
    ...apiParameters,
    ...additionalApiParameters,
    ...gridApiParameters,
  };

  const { data: fetchedData, isLoading } = useApiQuery<PaginatedResponse>(
    [apiUrl, serializeJSONToString(globalApiParameters)],
    null,
    {
      axiosConfig: {
        url: apiUrl,
        params: transformUrlSearchParams(globalApiParameters),
      },
    }
  );

  const data =
    apiUrl === config?.service?.url?.coreEnums ? timelineDataMock : fetchedData;

  const transformedData = useMemo(
    () => (dataTransformFunction ? dataTransformFunction(data) : data),
    [data, dataTransformFunction]
  );
  const paginationData = omit(data, ['content']);

  useEffect(() => {
    if (onDataLoaded) {
      onDataLoaded(data);
    }
  }, [data, onDataLoaded]);

  useEffect(() => {
    recoverGridParamsAC(gridStateId, paginationData, gridParams);
  }, [gridParams, gridStateId, paginationData, recoverGridParamsAC]);

  useEffect(() => {
    if (handlePaginationDataChange) {
      handlePaginationDataChange(paginationData.totalElements);
    }
  }, [handlePaginationDataChange, paginationData.totalElements]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  const debouncedChangeGridParamsAndQuery = useMemo(
    () => debounce(changeGridParamsAndQueryAC, 500),
    [changeGridParamsAndQueryAC]
  );

  const debouncedChangeMemorizeColumnStates = useMemo(
    () => debounce(changeMemorizeColumnStatesAC, 500),
    [changeMemorizeColumnStatesAC]
  );

  const handleSortChange = (sortModel) => {
    if (sortModel) {
      if (!isEqual(sortModel, currentSort)) {
        if (gridId) {
          const enhancedSortModel = enhanceGridSortWithAdditionalSort(
            sortModel,
            gridConfigModel.getAdditionalSortModel()
          );

          // change result sorting
          const params = {
            ...(!gridPagination || gridPagination.page !== 1 ? { page: 1 } : {}),
            sort: [...enhancedSortModel],
          };

          debouncedChangeGridParamsAndQuery(
            gridStateId,
            { ...params },
            filterAndSearchStateId,
            gridsApiStateId,
            apiUrl,
            params,
            featureName
          );
        }
      }
    }
  };

  const handlePageSizeChange = (size: number) => {
    if (gridPagination) {
      if (!isEqual(size, gridPagination.pageSize)) {
        if (gridId) {
          // change page size and reset page
          const params = {
            ...(!gridPagination || gridPagination.page !== 1 ? { page: 1 } : {}),
            size,
          };

          const pagination = {
            ...gridPagination,
            ...(!gridPagination || gridPagination.page !== 1 ? { page: 1 } : {}),
            pageSize: size,
          };

          // change component state
          debouncedChangeGridParamsAndQuery(
            gridStateId,
            { pagination },
            filterAndSearchStateId,
            gridsApiStateId,
            apiUrl,
            params,
            featureName
          );
        }
      }
    }
  };

  const handlePaginationChange = (pagination: Pagination) => {
    if (!isEqual(pagination.pageSize, gridPagination?.pageSize)) {
      if (gridId) {
        // change pagination
        const params = { size: pagination.pageSize };

        debouncedChangeGridParamsAndQuery(
          gridStateId,
          { pagination },
          filterAndSearchStateId,
          gridsApiStateId,
          apiUrl,
          params,
          featureName
        );
      }
    }
  };

  const handlePageChange = (page: number) => {
    if (!gridPagination) {
      return;
    }
    if (!isEqual(page, gridPagination.page)) {
      if (gridId) {
        // change page
        const params = { page };

        // change component state
        changeGridParamsAndQueryAC(
          gridStateId,
          params,
          filterAndSearchStateId,
          gridsApiStateId,
          apiUrl,
          params
        );
      }
    }
  };

  const handleColumnStatesChange = (columnStates: MemorizedColumnStates) => {
    debouncedChangeMemorizeColumnStates(
      gridStateId,
      filterAndSearchStateId,
      featureName,
      columnStates
    );
  };

  const agGridPagination: AgGridPagination | undefined = useMemo(() => {
    return (
      paginationData && {
        activePage: paginationData.number + 1,
        activeSize: paginationData.size,
        countInPage: paginationData.numberOfElements,
        page: paginationData.number + 1,
        pageSize: paginationData.size,
        pages: paginationData.totalPages,
        resultCount: paginationData.totalElements,
      }
    );
  }, [paginationData]);

  const paginationProps = disablePagination
    ? {}
    : {
      currentPagination: gridPagination,
      gridPagination,
      onPaginationChange: handlePaginationChange,
      handlePageChange,
      handlePageSizeChange,
      paginationData,
      agGridPagination,
    };

  return (
    // gridPagination has to be loaded before first render (if it is supplied)
    <AgGridWrapper
      rowsData={transformedData?.content}
      domLayout={domLayout}
      gridConfig={gridConfigModel}
      gridStateId={gridStateId}
      currentSort={currentSort}
      gridId={gridId}
      onSortChange={handleSortChange}
      disablePagination={disablePagination}
      selectedRow={selectedRow}
      showPageSize={showPageSize}
      handleColumnStatesChange={handleColumnStatesChange}
      memorizedColumnStates={memorizedColumnStates}
      {...paginationProps}
    />
  );
};

export default connector(FetchedDataGridContainer);
